import React, { useState } from 'react';
import { Star, Quote, ChevronDown } from 'lucide-react';
import { testimonials } from '../mock';

const Testimonials = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const initialCount = 3;
  // Show only 3 initially, show all if expanded
  const visibleTestimonials = isExpanded ? testimonials : testimonials.slice(0, initialCount);

  return (
    <section id="testimonials" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            What Our <span className="text-amber-700">Clients Say</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Don't just take our word for it - hear from families who made their dream homes a reality
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {visibleTestimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 relative animate-in fade-in slide-in-from-bottom-4"
            >
              {/* Quote Icon */}
              <div className="absolute -top-4 left-8">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center shadow-lg">
                  <Quote className="text-white" size={24} />
                </div>
              </div>

              {/* Stars */}
              <div className="flex items-center space-x-1 mb-6 mt-4">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={`${testimonial.id}-star-${i}`} 
                    className={i < testimonial.rating ? "text-amber-400 fill-amber-400" : "text-gray-200"} 
                    size={20} 
                  />
                ))}
              </div>

              <p className="text-gray-600 mb-8 leading-relaxed italic">
                "{testimonial.text}"
              </p>

              {/* Client Info */}
              <div className="flex items-center space-x-4 pt-6 border-t border-gray-100">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white font-bold text-xl ring-4 ring-amber-100">
                  {testimonial.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <div className="font-bold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View More Button */}
        {testimonials.length > initialCount && (
          <div className="mt-12 text-center">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="inline-flex items-center space-x-2 bg-amber-600 hover:bg-amber-700 text-white font-bold py-3 px-8 rounded-full transition-all duration-300 shadow-md active:scale-95 group"
            >
              <span>{isExpanded ? 'Show Less' : 'View More Reviews'}</span>
              <ChevronDown 
                size={20} 
                className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
              />
            </button>
          </div>
        )}

        {/* Trust Indicators */}
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-gray-100 pt-16">
          {[
            { label: 'Customer Satisfaction', value: '98%' },
            { label: 'Average Rating', value: '4.9/5' },
            { label: 'Repeat Customers', value: '95%' },
            { label: 'On-Time Delivery', value: '100%' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-4xl font-bold text-amber-600 mb-2">{stat.value}</div>
              <div className="text-gray-600 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;